import { useState, useEffect, useCallback, useRef } from 'react';
import { useI18n } from '../../../i18n';
import { ReactComponent as SearchIcon } from '../../../assets/icons/MagnifyingGlass.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/ArrowsCounterClockwise.svg';
import { ReactComponent as ExpandIcon } from '../../../assets/icons/ArrowsOutIn.svg';
import { useDashboard } from '../../../context/DashboardContext';
import folderService from '../../../services/folderService';
import { FaFilePdf, FaFileArchive, FaFileAlt, FaFileImage, FaFileWord, FaFileExcel, FaFile } from 'react-icons/fa';

const PER_PAGE = 15;

/* ── File-type icon mapper ── */
const FileIcon = ({ mimeType, extension }) => {
    const ext = (extension || '').toLowerCase();
    const mime = (mimeType || '').toLowerCase();

    if (ext === 'pdf' || mime.includes('pdf'))
        return <FaFilePdf style={{ color: '#ef4444' }} />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext))
        return <FaFileArchive style={{ color: '#f59e0b' }} />;
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext) || mime.startsWith('image/'))
        return <FaFileImage style={{ color: '#3b82f6' }} />;
    if (['doc', 'docx'].includes(ext))
        return <FaFileWord style={{ color: '#2563eb' }} />;
    if (['xls', 'xlsx', 'csv'].includes(ext))
        return <FaFileExcel style={{ color: '#16a34a' }} />;
    if (['txt', 'md', 'log'].includes(ext))
        return <FaFileAlt style={{ color: '#6366f1' }} />;
    return <FaFile style={{ color: '#94a3b8' }} />;
};

/* ── Skeleton ── */
const FilesSkeleton = () => (
    <>
        {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="file-entry file-skeleton">
                <div className="file-skel-icon" />
                <div className="file-skel-info">
                    <div className="file-skel-name" />
                    <div className="file-skel-date" />
                </div>
            </div>
        ))}
    </>
);

/* ── Date formatter ── */
const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;

    return `${day}-${month}-${year} ${displayHours}:${minutes} ${ampm}`;
};

const FilesWidget = () => {
    const { t } = useI18n();
    const { setActiveView } = useDashboard();

    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    /* ── Search State ── */
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const searchInputRef = useRef(null);
    const searchSeqRef = useRef(0);

    const inSearchMode = showSearch && searchQuery.length >= 2;
    const displayFiles = inSearchMode ? (searchResults ?? []) : files;
    const showSkeleton = isLoading || isRefreshing || (inSearchMode && isSearching && searchResults === null);

    /* ── Fetch page ── */
    const fetchPage = useCallback(async (nextPage, replace = false) => {
        try {
            const res = await folderService.getRecentFiles(nextPage, PER_PAGE);
            if (res?.status && res?.data) {
                const incoming = res.data.files || [];
                setFiles(prev => replace ? incoming : [...prev, ...incoming]);
                setHasMore(res.data.has_more ?? false);
                setPage(nextPage);
            }
        } catch (_) {
            // fail silently for widget
        }
    }, []);

    /* ── Initial load ── */
    useEffect(() => {
        setIsLoading(true);
        fetchPage(1, true).finally(() => setIsLoading(false));
    }, [fetchPage]);

    /* ── Debounced Search Logic ── */
    useEffect(() => {
        if (!showSearch || searchQuery.length < 2) {
            setSearchResults(null);
            setIsSearching(false);
            return;
        }

        const seq = ++searchSeqRef.current;
        setIsSearching(true);

        const timer = setTimeout(async () => {
            try {
                const res = await folderService.search(searchQuery);
                if (seq === searchSeqRef.current && res?.status) {
                    // Extract just the files from search results
                    setSearchResults(res.data.files || []);
                }
            } catch {
                if (seq === searchSeqRef.current) setSearchResults([]);
            } finally {
                if (seq === searchSeqRef.current) setIsSearching(false);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [searchQuery, showSearch]);

    const closeSearch = useCallback(() => {
        setShowSearch(false);
        setSearchQuery('');
        setSearchResults(null);
        setIsSearching(false);
    }, []);

    /* ── File Actions ── */
    const handleFileClick = (file) => {
        if (!file?.file_url) return;

        // Create a hidden anchor to trigger download
        const link = document.createElement('a');
        link.href = file.file_url;
        link.setAttribute('download', file.original_filename);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    /* ── Refresh ── */
    const handleRefresh = async () => {
        setIsRefreshing(true);
        if (inSearchMode) {
            try {
                const res = await folderService.search(searchQuery);
                if (res?.status) {
                    setSearchResults(res.data.files || []);
                }
            } catch (_) { }
        } else {
            await fetchPage(1, true);
        }
        setIsRefreshing(false);
    };

    /* ── Scroll-down infinite pagination ── */
    const handleScroll = useCallback(async (e) => {
        const el = e.currentTarget;
        if (!hasMore || loadingMore || isLoading || inSearchMode) return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
            setLoadingMore(true);
            await fetchPage(page + 1, false);
            setLoadingMore(false);
        }
    }, [hasMore, loadingMore, isLoading, page, fetchPage, inSearchMode]);

    /* ── Shared render pieces ── */
    const renderHeader = (onStretch) => (
        <div className={`widget-header files-header ${isExpanded ? 'nfw-expanded-header' : ''}`}>
            <div className="files-header-left">
                <span className="widget-title files-title">{t('pages.insights.files.title')}</span>
            </div>
            <div className="header-right">
                <SearchIcon
                    className={`widget-icon files-icon ${showSearch ? 'active' : ''}`}
                    title={t('pages.insights.files.search')}
                    onClick={() => {
                        if (showSearch) {
                            closeSearch();
                        } else {
                            setShowSearch(true);
                            setTimeout(() => searchInputRef.current?.focus(), 50);
                        }
                    }}
                />
                <RefreshIcon
                    className={`widget-icon files-icon${isRefreshing ? ' files-icon-spinning' : ''}`}
                    title={t('pages.insights.files.refresh')}
                    onClick={handleRefresh}
                />
                <ExpandIcon
                    className={`widget-icon files-icon ${isExpanded ? 'nfw-collapse-icon' : ''}`}
                    title={isExpanded ? t('pages.insights.files.collapse') : t('pages.insights.files.expand')}
                    onClick={onStretch}
                />
            </div>
        </div>
    );

    const renderSearchBar = () => (
        <div className="files-search-bar">
            <SearchIcon className="files-search-icon-sm" />
            <input
                ref={searchInputRef}
                className="files-search-input"
                placeholder={t('pages.insights.files.search_placeholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
            />
            {isSearching && <div className="files-search-spinner" />}
            <button className="files-search-close" onClick={closeSearch}>✕</button>
        </div>
    );

    const renderBody = (isActive = true) => (
        <div
            className="widget-body files-body"
            onScroll={isActive ? handleScroll : undefined}
        >
            {showSkeleton ? (
                <FilesSkeleton />
            ) : displayFiles.length === 0 ? (
                <div className="files-empty">
                    {inSearchMode ? t('pages.insights.files.no_results', { query: searchQuery }) : t('pages.insights.files.no_files')}
                </div>
            ) : (
                <>
                    {displayFiles.map(file => (
                        <div
                            key={file.id}
                            className="file-entry"
                            onClick={() => handleFileClick(file)}
                            title={file.original_filename}
                        >
                            <div className="file-pdf-icon">
                                <FileIcon mimeType={file.mime_type} extension={file.extension} />
                            </div>
                            <div className="file-info">
                                <div className="file-entry-name">{file.original_filename}</div>
                                <div className="file-entry-date">
                                    {file.file_size_human} · {formatDate(file.created_at)}
                                </div>
                            </div>
                        </div>
                    ))}
                    {!inSearchMode && loadingMore && (
                        <div className="files-load-more">
                            <div className="files-load-spinner" />
                        </div>
                    )}
                </>
            )}
        </div>
    );

    return (
        <>
            {/* Compact widget */}
            <div className="widget-card files-widget">
                {renderHeader(() => setIsExpanded(true))}
                {showSearch && renderSearchBar()}
                {renderBody(!isExpanded)}
            </div>

            {/* Expanded overlay */}
            {isExpanded && (
                <div className="nfw-expanded-overlay" onClick={() => setIsExpanded(false)}>
                    <div className="nfw-expanded-panel" onClick={e => e.stopPropagation()}>
                        {renderHeader(() => setIsExpanded(false))}
                        {showSearch && renderSearchBar()}
                        {renderBody(true)}
                    </div>
                </div>
            )}
        </>
    );
};

export default FilesWidget;
